import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Fade,
  Grow,
  Chip,
  Divider,
  Skeleton,
  useTheme,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import {
  Brain,
  MessageSquare,
  Smartphone,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Zap,
} from "lucide-react";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const TYPES = ["AI", "WABA", "RCS Media"];
const CATEGORY = "recharge";

const formatAmount = (amount) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);

const CreditStats = () => {
  // stats shape: { "ai": { totalCredit, totalDebit }, "waba": { ... }, ... }
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        TYPES.map((type) =>
          fetch(
            `${API_ENDPOINT}/credit/get-total-recharge-types-2?rechargeType=${type}&category=${CATEGORY}`,
            { method: "GET", ...getHeader() }
          )
            .then((res) => res.json())
            .then((data) => ({ type: type.toLowerCase(), ...data }))
        )
      );

      const newStats = {};
      results.forEach(({ type, totalCredit, totalDebit }) => {
        newStats[type] = { totalCredit, totalDebit };
      });
      setStats(newStats);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch credit stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    const iconProps = { size: 28 };
    switch (type) {
      case "ai":
        return <Brain {...iconProps} />;
      case "waba":
        return <MessageSquare {...iconProps} />;
      case "rcs media":
        return <Smartphone {...iconProps} />;
      default:
        return <Brain {...iconProps} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "ai":
        return theme.palette.primary.main;
      case "waba":
        return theme.palette.secondary.main;
      case "rcs media":
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Zap size={32} color={theme.palette.primary.main} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Credit Statistics
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {lastUpdated && (
            <Chip
              icon={<RefreshCw size={14} />}
              label={`Updated: ${lastUpdated.toLocaleTimeString()}`}
              variant="outlined"
              size="small"
            />
          )}
          <Button
            variant="outlined"
            onClick={fetchStats}
            startIcon={<RefreshCw size={18} />}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Loading skeleton */}
      {loading && (
        <Fade in={loading}>
          <Grid container spacing={3}>
            {TYPES.map((t) => (
              <Grid item xs={12} sm={6} md={4} key={t}>
                <Card sx={{ border: `1px solid ${theme.palette.divider}` }}>
                  <CardContent sx={{ p: 3 }}>
                    <Skeleton
                      variant="rounded"
                      width={44}
                      height={44}
                      sx={{ mb: 2 }}
                    />
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={28}
                      sx={{ mb: 2 }}
                    />
                    <Skeleton variant="text" width="80%" height={32} />
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={32}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Fade>
      )}

      {/* Statistics Grid */}
      {!loading && (
        <Fade in={!loading}>
          <Box>
            <Grid container spacing={3}>
              {TYPES.map((label, index) => {
                const key = label.toLowerCase();
                const typeColor = getTypeColor(key);
                const { totalCredit = 0, totalDebit = 0 } = stats[key] ?? {};

                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Grow in timeout={index * 200}>
                      <Card
                        sx={{
                          height: "100%",
                          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
                          border: `1px solid ${theme.palette.divider}`,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: theme.shadows[8],
                            borderColor: typeColor,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          {/* Icon + label */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 2,
                                backgroundColor: `${typeColor}15`,
                                color: typeColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {getTypeIcon(key)}
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {label}
                            </Typography>
                          </Box>

                          <Divider sx={{ mb: 2 }} />

                          {/* Credit row */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                color: theme.palette.success.main,
                              }}
                            >
                              <TrendingUp size={16} />
                              <Typography variant="body2" fontWeight={600}>
                                Credit
                              </Typography>
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "bold",
                                background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.text.primary})`,
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                color: "transparent",
                              }}
                            >
                              ₹{formatAmount(totalCredit)}
                            </Typography>
                          </Box>

                          {/* Debit row */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                color: theme.palette.error.main,
                              }}
                            >
                              <TrendingDown size={16} />
                              <Typography variant="body2" fontWeight={600}>
                                Debit
                              </Typography>
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: "bold",
                                background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.text.primary})`,
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                color: "transparent",
                              }}
                            >
                              ₹{formatAmount(totalDebit)}
                            </Typography>
                          </Box>

                          {/* Net row */}
                          {(() => {
                            const net = totalCredit - totalDebit;
                            const isPositive = net >= 0;
                            const netColor = isPositive
                              ? theme.palette.success.main
                              : theme.palette.error.main;
                            return (
                              <>
                                <Divider sx={{ my: 1.5 }} />
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: 1,
                                    backgroundColor: `${netColor}10`,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    sx={{ color: netColor }}
                                  >
                                    Net
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    fontWeight={700}
                                    sx={{ color: netColor }}
                                  >
                                    {isPositive ? "+" : "-"}₹
                                    {formatAmount(Math.abs(net))}
                                  </Typography>
                                </Box>
                              </>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    </Grow>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Fade>
      )}
    </Container>
  );
};

export default CreditStats;
